import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  RecurringTaskView,
  RecurrenceScheduleView,
  GeneralTicketTemplateView,
} from '../../../services/RecurringTaskService/types';
import {
  CpsiAccessRequestView,
  HstAccessRequestView,
  FacilityMaintenanceRequestView,
  PreventiveMaintenanceRequestView,
  CustomApplicationRequestView,
  ReportingRequestView,
} from '../../../types/shared/accessRequestTypes';
import {
  createDefaultCpsiAccessRequest,
  createDefaultHstAccessRequest,
  createDefaultFacilityMaintenanceRequest,
  createDefaultPreventiveMaintenanceRequest,
  createDefaultCustomApplicationRequest,
  createDefaultReportingRequest,
} from '../../../types/shared/requestDefaults';
import { recurringTaskQueriesApi } from '../../../services/RecurringTaskService/recurringTaskQueries';
import { recurringTaskMutationsApi } from '../../../services/RecurringTaskService/recurringTaskMutations';
import { createDefaultRecurringTask, normalizeRecurringTaskDates } from './recurringTaskTransforms';

// ── State Shape ────────────────────────────────────────────────────────

interface RecurringTaskState {
  currentRecurringTask: RecurringTaskView | null;
  selectedRecurringTaskId: string | null;
  isEdited: boolean;
  editedFields: string[];
}

const initialState: RecurringTaskState = {
  currentRecurringTask: null,
  selectedRecurringTaskId: null,
  isEdited: false,
  editedFields: [],
};

// ── Helpers ────────────────────────────────────────────────────────────

const addEditedField = (state: RecurringTaskState, fieldName: string) => {
  if (!state.editedFields.includes(fieldName)) {
    state.editedFields.push(fieldName);
  }
  state.isEdited = true;
};

// ── Slice ──────────────────────────────────────────────────────────────

const recurringTaskSlice = createSlice({
  name: 'recurringTask',
  initialState,
  reducers: {
    updateRecurringTask: (state, action: PayloadAction<Partial<RecurringTaskView>>) => {
      if (state.currentRecurringTask) {
        state.currentRecurringTask = { ...state.currentRecurringTask, ...action.payload };
        Object.keys(action.payload).forEach(field => {
          if (!state.editedFields.includes(field)) {
            state.editedFields.push(field);
          }
        });
        state.isEdited = true;
      }
    },

    initializeRecurringTask: (state) => {
      state.currentRecurringTask = createDefaultRecurringTask();
      state.isEdited = false;
      state.editedFields = [];
    },

    loadRecurringTask: (state, action: PayloadAction<RecurringTaskView>) => {
      state.currentRecurringTask = normalizeRecurringTaskDates(action.payload);
      state.selectedRecurringTaskId = action.payload.id || null;
      state.isEdited = false;
      state.editedFields = [];
    },

    clearRecurringTask: (state) => {
      state.currentRecurringTask = null;
      state.selectedRecurringTaskId = null;
      state.isEdited = false;
      state.editedFields = [];
    },

    setSelectedRecurringTaskId: (state, action: PayloadAction<string | null>) => {
      state.selectedRecurringTaskId = action.payload;
    },

    // ── Template ─────────────────────────────────────────────────────

    updateTemplate: (state, action: PayloadAction<{
      templateTitle?: string;
      category?: string;
    }>) => {
      if (state.currentRecurringTask) {
        if (action.payload.templateTitle !== undefined) {
          state.currentRecurringTask.templateTitle = action.payload.templateTitle;
        }
        if (action.payload.category !== undefined) {
          state.currentRecurringTask.category = action.payload.category;
        }
        addEditedField(state, 'template');
      }
    },

    // ── Schedule ─────────────────────────────────────────────────────

    updateSchedule: (state, action: PayloadAction<Partial<RecurrenceScheduleView>>) => {
      if (state.currentRecurringTask) {
        state.currentRecurringTask.schedule = {
          ...state.currentRecurringTask.schedule,
          ...action.payload,
        };
        addEditedField(state, 'schedule');
      }
    },

    // ── Priority ─────────────────────────────────────────────────────

    updatePriority: (state, action: PayloadAction<string>) => {
      if (state.currentRecurringTask) {
        state.currentRecurringTask.priority = action.payload;
        addEditedField(state, 'priority');
      }
    },

    // ── Assignment ───────────────────────────────────────────────────

    updateAssignment: (state, action: PayloadAction<{
      defaultAssignee?: string;
      defaultAssigneeName?: string;
      defaultTeam?: string;
    }>) => {
      if (state.currentRecurringTask) {
        if (action.payload.defaultAssignee !== undefined) {
          state.currentRecurringTask.defaultAssignee = action.payload.defaultAssignee;
        }
        if (action.payload.defaultAssigneeName !== undefined) {
          state.currentRecurringTask.defaultAssigneeName = action.payload.defaultAssigneeName;
        }
        if (action.payload.defaultTeam !== undefined) {
          state.currentRecurringTask.defaultTeam = action.payload.defaultTeam;
        }
        addEditedField(state, 'assignment');
      }
    },

    // ── Lifecycle ────────────────────────────────────────────────────

    updateLifecycle: (state, action: PayloadAction<{
      startDate?: string;
      endDate?: string;
      maxOccurrences?: number;
      isActive?: boolean;
    }>) => {
      if (state.currentRecurringTask) {
        if (action.payload.startDate !== undefined) {
          state.currentRecurringTask.startDate = action.payload.startDate;
        }
        if (action.payload.endDate !== undefined) {
          state.currentRecurringTask.endDate = action.payload.endDate;
        }
        if (action.payload.maxOccurrences !== undefined) {
          state.currentRecurringTask.maxOccurrences = action.payload.maxOccurrences;
        }
        if (action.payload.isActive !== undefined) {
          state.currentRecurringTask.isActive = action.payload.isActive;
        }
        addEditedField(state, 'lifecycle');
      }
    },

    // ── Access Requests ──────────────────────────────────────────────

    updateHstAccessRequest: (state, action: PayloadAction<Partial<HstAccessRequestView>>) => {
      if (state.currentRecurringTask) {
        if (!state.currentRecurringTask.hstAccessRequest) {
          state.currentRecurringTask.hstAccessRequest = createDefaultHstAccessRequest();
        }
        state.currentRecurringTask.hstAccessRequest = {
          ...state.currentRecurringTask.hstAccessRequest,
          ...action.payload,
        };
        addEditedField(state, 'hstAccessRequest');
      }
    },

    clearHstAccessRequest: (state) => {
      if (state.currentRecurringTask) {
        state.currentRecurringTask.hstAccessRequest = undefined;
        addEditedField(state, 'hstAccessRequest');
      }
    },

    updateCpsiAccessRequest: (state, action: PayloadAction<Partial<CpsiAccessRequestView>>) => {
      if (state.currentRecurringTask) {
        if (!state.currentRecurringTask.cpsiAccessRequest) {
          state.currentRecurringTask.cpsiAccessRequest = createDefaultCpsiAccessRequest();
        }
        state.currentRecurringTask.cpsiAccessRequest = {
          ...state.currentRecurringTask.cpsiAccessRequest,
          ...action.payload,
        };
        addEditedField(state, 'cpsiAccessRequest');
      }
    },

    clearCpsiAccessRequest: (state) => {
      if (state.currentRecurringTask) {
        state.currentRecurringTask.cpsiAccessRequest = undefined;
        addEditedField(state, 'cpsiAccessRequest');
      }
    },

    updateFacilityMaintenanceRequest: (state, action: PayloadAction<Partial<FacilityMaintenanceRequestView>>) => {
      if (state.currentRecurringTask) {
        if (!state.currentRecurringTask.facilityMaintenanceRequest) {
          state.currentRecurringTask.facilityMaintenanceRequest = createDefaultFacilityMaintenanceRequest();
        }
        state.currentRecurringTask.facilityMaintenanceRequest = {
          ...state.currentRecurringTask.facilityMaintenanceRequest,
          ...action.payload,
        };
        addEditedField(state, 'facilityMaintenanceRequest');
      }
    },

    clearFacilityMaintenanceRequest: (state) => {
      if (state.currentRecurringTask) {
        state.currentRecurringTask.facilityMaintenanceRequest = undefined;
        addEditedField(state, 'facilityMaintenanceRequest');
      }
    },

    // ── Custom Application Request ───────────────────────────────────

    updateCustomApplicationRequest: (state, action: PayloadAction<Partial<CustomApplicationRequestView>>) => {
      if (state.currentRecurringTask) {
        if (!state.currentRecurringTask.customApplicationRequest) {
          state.currentRecurringTask.customApplicationRequest = createDefaultCustomApplicationRequest();
        }
        state.currentRecurringTask.customApplicationRequest = {
          ...state.currentRecurringTask.customApplicationRequest,
          ...action.payload,
        };
        addEditedField(state, 'customApplicationRequest');
      }
    },

    clearCustomApplicationRequest: (state) => {
      if (state.currentRecurringTask) {
        state.currentRecurringTask.customApplicationRequest = undefined;
        addEditedField(state, 'customApplicationRequest');
      }
    },

    // ── Reporting Request ───────────────────────────────────────

    updateReportingRequest: (state, action: PayloadAction<Partial<ReportingRequestView>>) => {
      if (state.currentRecurringTask) {
        if (!state.currentRecurringTask.reportingRequest) {
          state.currentRecurringTask.reportingRequest = createDefaultReportingRequest();
        }
        state.currentRecurringTask.reportingRequest = {
          ...state.currentRecurringTask.reportingRequest,
          ...action.payload,
        };
        addEditedField(state, 'reportingRequest');
      }
    },

    clearReportingRequest: (state) => {
      if (state.currentRecurringTask) {
        state.currentRecurringTask.reportingRequest = undefined;
        addEditedField(state, 'reportingRequest');
      }
    },

    // ── General Ticket Template ──────────────────────────────────────

    updateGeneralTicketTemplate: (state, action: PayloadAction<Partial<GeneralTicketTemplateView>>) => {
      if (state.currentRecurringTask) {
        if (!state.currentRecurringTask.generalTicketTemplate) {
          state.currentRecurringTask.generalTicketTemplate = {
            ticketTitle: '',
            ticketDescription: '',
          };
        }
        state.currentRecurringTask.generalTicketTemplate = {
          ...state.currentRecurringTask.generalTicketTemplate,
          ...action.payload,
        };
        addEditedField(state, 'generalTicketTemplate');
      }
    },

    clearGeneralTicketTemplate: (state) => {
      if (state.currentRecurringTask) {
        state.currentRecurringTask.generalTicketTemplate = undefined;
        addEditedField(state, 'generalTicketTemplate');
      }
    },

    // ── Preventive Maintenance ───────────────────────────────────────

    updatePreventiveMaintenanceRequest: (state, action: PayloadAction<Partial<PreventiveMaintenanceRequestView>>) => {
      if (state.currentRecurringTask) {
        if (!state.currentRecurringTask.preventiveMaintenanceRequest) {
          state.currentRecurringTask.preventiveMaintenanceRequest = createDefaultPreventiveMaintenanceRequest();
        }
        state.currentRecurringTask.preventiveMaintenanceRequest = {
          ...state.currentRecurringTask.preventiveMaintenanceRequest,
          ...action.payload,
        };
        addEditedField(state, 'preventiveMaintenanceRequest');
      }
    },

    clearPreventiveMaintenanceRequest: (state) => {
      if (state.currentRecurringTask) {
        state.currentRecurringTask.preventiveMaintenanceRequest = undefined;
        addEditedField(state, 'preventiveMaintenanceRequest');
      }
    },
  },

  // ── Extra Reducers ─────────────────────────────────────────────────

  extraReducers: (builder) => {
    builder.addMatcher(
      recurringTaskQueriesApi.endpoints.getRecurringTaskById.matchFulfilled,
      (state, action) => {
        state.currentRecurringTask = action.payload;
        state.selectedRecurringTaskId = action.payload.id || null;
        state.isEdited = false;
        state.editedFields = [];
      }
    );
    builder.addMatcher(
      recurringTaskMutationsApi.endpoints.upsertRecurringTask.matchFulfilled,
      (state, action) => {
        state.currentRecurringTask = action.payload;
        state.selectedRecurringTaskId = action.payload.id || null;
        state.isEdited = false;
        state.editedFields = [];
      }
    );
  },
});

export const {
  updateRecurringTask,
  initializeRecurringTask,
  loadRecurringTask,
  clearRecurringTask,
  setSelectedRecurringTaskId,
  updateTemplate,
  updateSchedule,
  updatePriority,
  updateAssignment,
  updateLifecycle,
  updateHstAccessRequest,
  clearHstAccessRequest,
  updateCpsiAccessRequest,
  clearCpsiAccessRequest,
  updateFacilityMaintenanceRequest,
  clearFacilityMaintenanceRequest,
  updateCustomApplicationRequest,
  clearCustomApplicationRequest,
  updateReportingRequest,
  clearReportingRequest,
  updateGeneralTicketTemplate,
  clearGeneralTicketTemplate,
  updatePreventiveMaintenanceRequest,
  clearPreventiveMaintenanceRequest,
} = recurringTaskSlice.actions;

export default recurringTaskSlice.reducer;
